/**
 * JWT Helper Utilities
 * Provides lightweight JWT decoding without external dependencies
 */

/**
 * Decode a JWT token to extract payload
 * @param {string} token - JWT token
 * @returns {object|null} Decoded payload or null if invalid
 */
export function decodeJWT(token) {
  if (!token || typeof token !== 'string') return null;

  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1];
    // Replace URL-safe characters and decode base64
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('JWT decode error:', error);
    return null;
  }
}

/**
 * Check if a JWT token is expired
 * @param {string} token - JWT token
 * @returns {boolean} True if expired
 */
export function isTokenExpired(token) {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) return true;

  const now = Math.floor(Date.now() / 1000);
  return decoded.exp < now;
}

/**
 * Get seconds until token expiry
 * @param {string} token - JWT token
 * @returns {number} Seconds until expiry, or 0 if expired/invalid
 */
export function getSecondsUntilExpiry(token) {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) return 0;

  const now = Math.floor(Date.now() / 1000);
  return Math.max(decoded.exp - now, 0);
}

/**
 * Calculate when to refresh the token (2 minutes before expiry)
 * @param {string} token - JWT token
 * @returns {number} Milliseconds until refresh should happen
 */
export function getRefreshDelay(token) {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) return 0;

  const now = Math.floor(Date.now() / 1000);
  const refreshAt = Math.max(decoded.exp - 120, now); // 2 min before expiry
  const delay = Math.max((refreshAt - now) * 1000, 0);

  return delay;
}
